import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FiEdit2, FiArrowLeft, FiCheck } from 'react-icons/fi';
import ContestService from '../services/ContestService';

const EditContest = () => {
  const { id } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    type: 'Music & Singing',
    categorySlug: 'music-singing',
    prizeonewords: '',
    expiryDate: '',
    status: 'LIVE',
    about: '',
    bannerUrl: '',
    posterUrl: '',
    frameUrl: '',
    isPaid: false,
    entryFee: 0,
    isFrameAllow: true,
    attachedEventId: '',
  });

  useEffect(() => {
    fetchContestData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchContestData = async () => {
    setLoading(true);
    try {
      const res = await ContestService.getContestById(id).catch(() => null);
      if (res) {
        const c = res.contest || res.event || res.eventDetails || res;
        const paidVal = c.isPaid === true || (c.entrySettings && c.entrySettings.isPaid === true) || (Number(c.entryFee || c.entrySettings?.feeAmount) > 0);
        const feeVal = Number(c.entryFee || c.entrySettings?.feeAmount || 0);
        const frameVal = c.frameUrl || c.votingSettings?.frameUrl || '';
        const frameAllowVal = c.isFrameAllow !== undefined ? Boolean(c.isFrameAllow) : true;

        setFormData({
          title: c.title || '',
          type: c.type || c.category || 'Music & Singing',
          categorySlug: c.categorySlug || 'music-singing',
          prizeonewords: c.prizeonewords || c.prize || '',
          expiryDate: c.expiryDate ? c.expiryDate.split('T')[0] : '',
          status: c.status || 'LIVE',
          about: c.about || c.description || '',
          bannerUrl: c.bannerUrl || c.banner?.url || '',
          posterUrl: c.posterUrl || c.poster?.url || '',
          frameUrl: frameVal,
          isPaid: paidVal,
          entryFee: feeVal,
          isFrameAllow: frameAllowVal,
          attachedEventId: c.eventId || c.attachedEventId || '',
        });
      }
    } catch (err) {
      console.error('Error fetching contest for edit:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      alert('Please enter contest title.');
      return;
    }

    setSaving(true);
    try {
      const feeVal = formData.isPaid ? (Number(formData.entryFee) || 0) : 0;
      await ContestService.updateContest(id, {
        ...formData,
        entryFee: feeVal,
        isPaid: formData.isPaid,
        entrySettings: {
          isPaid: formData.isPaid,
          feeAmount: feeVal,
        },
        votingSettings: {
          IsFrameAllow: formData.isFrameAllow,
          frameUrl: formData.isFrameAllow ? formData.frameUrl : '',
        },
        banner: { url: formData.bannerUrl },
        poster: { url: formData.posterUrl },
      });
      alert('Contest updated successfully!');
      history.push('/app/contests');
    } catch (err) {
      console.error('Error updating contest:', err);
      const errMsg = err.response?.data?.message || err.message || 'Failed to update contest.';
      alert(`Error: ${errMsg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 text-left pb-20">
      {/* Back Header */}
      <div className="flex items-center gap-4 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <button
          onClick={() => history.goBack()}
          className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors"
        >
          <FiArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
            <FiEdit2 className="text-purple-700" /> Edit Contest Configuration
          </h1>
          <p className="text-xs font-medium text-slate-500 mt-1">Update contest details, banner images, category, rules & end dates</p>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-slate-500 text-xs font-bold">Loading contest data...</div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <div className="space-y-4">
            {/* Contest Title */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Contest Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>

            {/* Grid 1: Category & Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Category / Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                >
                  <option value="Music & Singing">Music & Singing</option>
                  <option value="Dance & Performance">Dance & Performance</option>
                  <option value="Fashion & Modeling">Fashion & Modeling</option>
                  <option value="Photography & Art">Photography & Art</option>
                  <option value="Technology & Hackathon">Technology & Hackathon</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Contest Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                >
                  <option value="LIVE">LIVE (Active)</option>
                  <option value="PAUSED">PAUSED (Voting Suspended)</option>
                  <option value="EXPIRED">EXPIRED (Ended)</option>
                </select>
              </div>
            </div>

            {/* Grid 2: Prize & Expiry Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Prize / Reward Details</label>
                <input
                  type="text"
                  value={formData.prizeonewords}
                  onChange={(e) => setFormData({ ...formData, prizeonewords: e.target.value })}
                  placeholder="e.g. ₹25,000 Cash Prize + Trophy"
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Expiry Date</label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
                />
              </div>
            </div>

            {/* Frame URL & Pricing Model */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Photo Frame Overlay PNG URL</label>
                <input
                  type="url"
                  value={formData.frameUrl}
                  onChange={(e) => setFormData({ ...formData, frameUrl: e.target.value })}
                  placeholder="https://.../frame.png"
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Contest Entry Pricing</label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs font-bold text-slate-800 cursor-pointer">
                    <input
                      type="radio"
                      name="isPaidEdit"
                      checked={!formData.isPaid}
                      onChange={() => setFormData({ ...formData, isPaid: false, entryFee: 0 })}
                      className="accent-emerald-600"
                    />
                    <span>Free Entry</span>
                  </label>
                  <label className="flex items-center gap-1.5 text-xs font-bold text-slate-800 cursor-pointer">
                    <input
                      type="radio"
                      name="isPaidEdit"
                      checked={formData.isPaid}
                      onChange={() => setFormData({ ...formData, isPaid: true })}
                      className="accent-purple-600"
                    />
                    <span>Paid Entry (₹)</span>
                  </label>
                </div>
                {formData.isPaid && (
                  <input
                    type="number"
                    min="1"
                    value={formData.entryFee}
                    onChange={(e) => setFormData({ ...formData, entryFee: e.target.value })}
                    placeholder="Entry fee (₹)"
                    className="w-full bg-white border border-purple-300 rounded-xl p-2.5 text-xs text-purple-900 outline-none focus:border-purple-600 font-bold mt-2"
                  />
                )}
              </div>
            </div>

            {/* Grid 3: Banner & Poster Image URLs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Banner Cover Image URL</label>
                <input
                  type="url"
                  value={formData.bannerUrl}
                  onChange={(e) => setFormData({ ...formData, bannerUrl: e.target.value })}
                  placeholder="https://..."
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Poster Card Image URL</label>
                <input
                  type="url"
                  value={formData.posterUrl}
                  onChange={(e) => setFormData({ ...formData, posterUrl: e.target.value })}
                  placeholder="https://..."
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />
              </div>
            </div>

            {/* Description & Rules */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">About & Submission Rules</label>
              <textarea
                rows={5}
                value={formData.about}
                onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                placeholder="Explain contest rules, voting rules, and submission guidelines..."
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => history.goBack()}
              className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-bold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-2"
            >
              <FiCheck className="w-4 h-4" />
              <span>Save Contest Changes</span>
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EditContest;
